import * as yup from 'yup'

export const RILLET_WEBHOOK_MODES = {
  DEFAULT: 'DEFAULT',
  DELETED: 'DELETED',
} as const

export type RilletWebhookMode = (typeof RILLET_WEBHOOK_MODES)[keyof typeof RILLET_WEBHOOK_MODES]

export const rilletWebhookQuerySchema = yup.object({
  organizationId: yup.string().required(),
})

export const rilletWebhookEntityEventSchema = yup.object().shape({
  'x-rillet-entity': yup.string().required(),
  'x-rillet-event': yup.string().required(),
})

export const rilletAddressWebhookDataSchema = yup.object().shape({
  line1: yup.string().nullable().optional(),
  line2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zip_code: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})

const rilletVendorWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  updated_at: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  account_code: yup.string().nullable().optional(),
  address: rilletAddressWebhookDataSchema.nullable().optional(),
  email: yup.string().email().nullable().optional(),
  payment_terms: yup.number().nullable().optional(),
  external_references: yup.array().of(
    yup.object().shape({
      id: yup.string().nullable().optional(),
      type: yup.string().nullable().optional(),
      url: yup.string().url().nullable().optional(),
    }),
  ),
  ten_ninety_nine_eligible: yup.boolean().nullable().optional(),
  tax_id: yup.string().nullable().optional(),
  fields: yup.array().of(
    yup.object().shape({
      field_id: yup.string().nullable().optional(),
      field_value_id: yup.string().nullable().optional(),
    }),
  ),
}

export const rilletVendorWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletVendorWebhookDataSchema,
})

export type RilletVendorData = yup.InferType<typeof rilletVendorWebhookSchema>

export const rilletVendorDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletVendorDeletedData = yup.InferType<typeof rilletVendorDeletedWebhookSchema>

const rilletMonetaryAmountNullableWebhookSchema = yup
  .object()
  .shape({
    amount: yup.string().nullable().optional(),
    currency: yup.string().nullable().optional(),
  })
  .nullable()
  .optional()

const rilletContractItemWebhookSchema = yup.object().shape({
  id: yup.string().nullable().optional(),
  product_id: yup.string().nullable().optional(),
  quantity: yup.number().nullable().optional(),
  total_value: rilletMonetaryAmountNullableWebhookSchema,
  revenue_pattern: yup.string().nullable().optional(),
  start_date: yup.string().nullable().optional(),
  end_date: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  external_references: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().nullable().optional(),
        type: yup.string().nullable().optional(),
        url: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
})

export const rilletContractWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  customer_id: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  start_date: yup.string().nullable().optional(),
  end_date: yup.string().nullable().optional(),
  close_date: yup.string().nullable().optional(),
  total_value: rilletMonetaryAmountNullableWebhookSchema,
  items: yup.array().of(rilletContractItemWebhookSchema).nullable().optional(),
  external_references: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().nullable().optional(),
        type: yup.string().nullable().optional(),
        url: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  invoices: yup.array().of(yup.object().nullable().optional()).nullable().optional(),
}

export const rilletContractWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletContractWebhookDataSchema,
})

export type RilletContractData = yup.InferType<typeof rilletContractWebhookSchema>

export const rilletContractDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletContractDeletedData = yup.InferType<typeof rilletContractDeletedWebhookSchema>

export const rilletCustomerWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  updated_at: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  name_on_invoices: yup.string().nullable().optional(),
  address: rilletAddressWebhookDataSchema.nullable().optional(),
  shipping_address: rilletAddressWebhookDataSchema.nullable().optional(),
  emails: yup.array().of(
    yup.object().shape({
      email: yup.string().email().nullable().optional(),
      type: yup.string().nullable().optional(),
    }),
  ),
  external_references: yup.array().of(
    yup.object().shape({
      id: yup.string().nullable().optional(),
      type: yup.string().nullable().optional(),
      url: yup.string().url().nullable().optional(),
    }),
  ),
  payment_terms: yup.number().nullable().optional(),
  send_invoices_automatically: yup.boolean().nullable().optional(),
  send_payment_reminders: yup.boolean().nullable().optional(),
  fields: yup.array().of(
    yup.object().shape({
      field_id: yup.string().nullable().optional(),
      field_value_id: yup.string().nullable().optional(),
    }),
  ),
}

export const rilletCustomerWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletCustomerWebhookDataSchema,
})

export type RilletCustomerData = yup.InferType<typeof rilletCustomerWebhookSchema>

export const rilletCustomerDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletCustomerDeletedData = yup.InferType<typeof rilletCustomerDeletedWebhookSchema>

const rilletBillWebhookDataSchema = {
  id: yup.string().optional().nullable().optional(),
  status: yup
    .string()
    .oneOf(['UNPAID', 'PAID', 'PARTIALLY_PAID', 'CREDITED', 'PARTIALLY_CREDITED', 'APPLIED'])
    .optional()
    .nullable()
    .optional(),
  updated_at: yup.string().nullable().optional(),
  vendor_id: yup.string().nullable().optional(),
  expense_number: yup.string().nullable().optional(),
  items: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().optional().nullable().optional(),
        description: yup.string().nullable().optional(),
        account_code: yup.string().nullable().optional(),
        amount: yup
          .object()
          .shape({
            amount: yup.string().nullable().optional(),
            currency: yup.string().nullable().optional(),
          })
          .required(),
        tax_rate: yup
          .object()
          .shape({
            percentage: yup.number().nullable().optional(),
            tax_amount: yup
              .object()
              .shape({
                amount: yup.string().nullable().optional(),
                currency: yup.string().nullable().optional(),
              })
              .nullable()
              .optional(),
            country: yup.string().nullable().optional(),
            type: yup.string().oneOf(['VAT', 'SALES_TAX']).optional().nullable().optional(),
            description: yup.string().nullable().optional(),
            coverage: yup.string().oneOf(['INCLUSIVE', 'EXCLUSIVE']).nullable().optional(),
          })
          .nullable()
          .optional(),
        service_period: yup
          .object()
          .shape({
            start: yup.string().nullable().optional(),
            end: yup.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        fields: yup
          .array()
          .of(
            yup.object().shape({
              field_id: yup.string().nullable().optional(),
              field_value_id: yup.string().nullable().optional(),
            }),
          )
          .nullable()
          .optional(),
      }),
    )
    .nullable()
    .optional(),
  bill_date: yup.string().nullable().optional(),
  due_date: yup.string().nullable().optional(),
  impact_date: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  external_references: yup
    .array()
    .of(
      yup.object().shape({
        type: yup.string().nullable().optional(),
        id: yup.string().nullable().optional(),
        url: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  exchange_rate: yup
    .object()
    .shape({
      base: yup.string().nullable().optional(),
      target: yup.string().nullable().optional(),
      rate: yup.string().nullable().optional(),
      date: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
}

export const rilletBillWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletBillWebhookDataSchema,
})

export type RilletBillData = yup.InferType<typeof rilletBillWebhookSchema>

export const rilletBillDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletBillDeletedData = yup.InferType<typeof rilletBillDeletedWebhookSchema>

const rilletMonetaryAmountDataSchema = yup.object().shape({
  amount: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
})

const rilletExchangeRateDataSchema = yup.object().shape({
  base: yup.string().nullable().optional(),
  target: yup.string().nullable().optional(),
  rate: yup.string().nullable().optional(),
  date: yup.string().nullable().optional(),
})

const rilletJournalEntryItemDataSchema = yup.object().shape({
  id: yup.string().nullable().optional(),
  account_code: yup.string().nullable().optional(),
  amount: rilletMonetaryAmountDataSchema.nullable().optional(),
  description: yup.string().nullable().optional(),
  side: yup.string().nullable().optional(),
  fields: yup
    .array()
    .of(
      yup.object().shape({
        field_id: yup.string().nullable().optional(),
        field_value_id: yup.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
})

const rilletJournalEntryWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  related_entity: yup
    .object()
    .shape({
      id: yup.string().nullable().optional(),
      type: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  items: yup.array().of(rilletJournalEntryItemDataSchema).nullable().optional(),
  date: yup.string().nullable().optional(),
  reversal_date: yup.string().nullable().optional(),
  attachmentUrl: yup.string().nullable().optional(),
  exchange_rate: rilletExchangeRateDataSchema.nullable().optional(),
  updated_at: yup.string().nullable().optional(),
}

export const rilletJournalEntryWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletJournalEntryWebhookDataSchema,
})

export type RilletJournalEntryData = yup.InferType<typeof rilletJournalEntryWebhookSchema>

export const rilletJournalEntryDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletJournalEntryDeletedData = yup.InferType<
  typeof rilletJournalEntryDeletedWebhookSchema
>

const rilletExternalReferenceSchema = yup
  .object({
    id: yup.string().nullable().optional(),
    type: yup.string().nullable().optional(),
    url: yup.string().url().nullable().optional(),
  })
  .nullable()
  .optional()

const rilletFieldSchema = yup
  .object({
    field_id: yup.string().nullable().optional(),
    field_value_id: yup.string().nullable().optional(),
  })
  .nullable()
  .optional()

const itemSchema = yup
  .object({
    id: yup.string().nullable().optional(),
    description: yup.string().nullable().optional(),
    revenue: yup
      .object({
        period: yup
          .object({
            start: yup.string().nullable().optional(),
            end: yup.string().nullable().optional(),
          })
          .nullable()
          .optional(),
        pattern: yup.string().nullable().optional(),
        account_code: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    total_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
    total_discount: rilletMonetaryAmountDataSchema.nullable().optional(),
    taxed_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
    gross_rate: rilletMonetaryAmountDataSchema.nullable().optional(),
    quantity: yup.number().nullable().optional(),
    product_id: yup.string().nullable().optional(),
    external_references: yup
      .array()
      .of(rilletExternalReferenceSchema.nullable().optional())
      .nullable()
      .optional(),

    fields: yup.array().of(rilletFieldSchema).nullable().optional(),
  })
  .nullable()
  .optional()

export const rilletInvoiceWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  invoice_number: yup.string().nullable().optional(),
  customer_id: yup.string().nullable().optional(),
  contract_id: yup.string().nullable().optional(),
  items: yup.array().of(itemSchema).nullable().optional(),
  due_date: yup.string().nullable().optional(),
  invoice_date: yup.string().nullable().optional(),
  address: rilletAddressWebhookDataSchema.nullable().optional(),
  customer_address: rilletAddressWebhookDataSchema.nullable().optional(),
  shipping_address: rilletAddressWebhookDataSchema.nullable().optional(),
  bank_account: yup
    .object({
      id: yup.string().nullable().optional(),
      ach_account: yup.string().nullable().optional(),
      ach_routing: yup.string().nullable().optional(),
      bank_name: yup.string().nullable().optional(),
      bank_address: yup.string().nullable().optional(),
      swift_code: yup.string().nullable().optional(),
      payment_instructions: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  status: yup.string().nullable().optional(),
  net_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
  amount_due: rilletMonetaryAmountDataSchema.nullable().optional(),
  total: rilletMonetaryAmountDataSchema.nullable().optional(),
  payment_terms: yup.number().nullable().optional(),
  subtotal: rilletMonetaryAmountDataSchema.nullable().optional(),
  total_discount: rilletMonetaryAmountDataSchema.nullable().optional(),
  purchase_order_number: yup.string().nullable().optional(),
  exchange_rate: rilletExchangeRateDataSchema.nullable().optional(),
  taxed_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
  memo: yup.string().nullable().optional(),
  overdue: yup.boolean().nullable().optional(),
  external_references: yup
    .array()
    .of(rilletExternalReferenceSchema.nullable().optional())
    .nullable()
    .optional(),
  updated_at: yup.string().nullable().optional(),
}

export const rilletInvoiceWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletInvoiceWebhookDataSchema,
})

export type RilletInvoiceData = yup.InferType<typeof rilletInvoiceWebhookSchema>

export const rilletInvoiceDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletInvoiceDeletedData = yup.InferType<typeof rilletInvoiceDeletedWebhookSchema>

const rilletCreditMemoWebhookDataSchema = {
  id: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  credit_memo_number: yup.string().nullable().optional(),
  customer_id: yup.string().nullable().optional(),
  invoice_id: yup.string().nullable().optional(),
  credit_memo_date: yup.string().nullable().optional(),
  updated_at: yup.string().nullable().optional(),
  address: rilletAddressWebhookDataSchema.nullable().optional(),
  items: yup
    .array()
    .of(
      yup.object({
        id: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        quantity: yup.number().nullable().optional(),
        product_id: yup.string().nullable().optional(),
        revenue: yup
          .object({
            pattern: yup.string().nullable().optional(),
            account_code: yup.string().nullable().optional(),
            period: yup
              .object({
                start: yup.string().nullable().optional(),
                end: yup.string().nullable().optional(),
              })
              .nullable()
              .optional(),
          })
          .nullable()
          .optional(),
        total_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
        taxed_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
        net_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
        fields: rilletFieldSchema.nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  net_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
  total: rilletMonetaryAmountDataSchema.nullable().optional(),
  taxed_amount: rilletMonetaryAmountDataSchema.nullable().optional(),
  exchange_rate: rilletExchangeRateDataSchema.nullable().optional(),
  external_references: yup
    .array()
    .of(rilletExternalReferenceSchema.nullable().optional())
    .nullable()
    .optional(),
}

export const rilletCreditMemoWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  ...rilletCreditMemoWebhookDataSchema,
})

export type RilletCreditMemoData = yup.InferType<typeof rilletCreditMemoWebhookSchema>

export const rilletCreditMemoDeletedWebhookSchema = yup.object().shape({
  doss_org_id: yup.string().required(),
  webhook_topic: yup.string().required(),
  id: yup.string().required(),
})

export type RilletCreditMemoDeletedData = yup.InferType<typeof rilletCreditMemoDeletedWebhookSchema>
